// =====================================================================
//  Onglet « Contrat d'appel » d'une stratégie OTP (lecture seule).
//  Documente, dans le BO, ce que l'application appelante doit envoyer à
//  l'exécution : strategyId + payload runtime (mail, tel, contexte…).
//  Sépare visuellement « paramètres BO » de « paramètres runtime ».
// =====================================================================

import {IDisposableLike} from "../../Tools/Disposable/IDisposableLike";
import {IOtpStrategyModel} from "../../Model/Otp/OtpStrategyModel.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";

export class OtpContractEditor implements IDisposableLike {
    #jqMainContainer: JQuery | undefined;
    #model: IOtpStrategyModel;

    constructor(options: IOtpContractEditorOptions) {
        this.#jqMainContainer = options.jqMainContainer;
        this.#model = options.model;
    }

    public Init = () => {
        this.#buildHtml();
    };

    #payloadExample = (): string => {
        const payload = {
            strategyId: this.#model.Primarykey ?? "(enregistrer pour obtenir l'Id)",
            target: {
                mail: "utilisateur@exemple.fr",
                tel: "+33600000000",
            },
            // Le contexte alimente les variables du template (hors variables
            // réservées). Tout ce que le template attend — Nom, Prénom,
            // NumCommande… — se place ici.
            context: {
                Nom: "DUPONT",
                Prenom: "Camille",
                NumCommande: "CMD-2026-0042",
            },
        };
        return JSON.stringify(payload, null, 2);
    };

    #verifyExample = (): string => {
        const ret = {
            Status: 200,
            Data: {
                Verified: true,
                RemainingAttempts: null,
                Reason: null,
            },
        };
        return JSON.stringify(ret, null, 2);
    };

    #buildHtml = () => {
        const wrap = $("<div></div>")
            .css({padding: "12px", maxWidth: "760px"})
            .appendTo(this.#jqMainContainer);

        $("<p></p>")
            .css({color: "#555", marginTop: 0})
            .html(
                "Ce contrat décrit l'appel que l'<b>application distante</b> doit émettre vers EASYMAIL " +
                "pour déclencher cette stratégie. Les paramètres ci-dessous sont fournis <b>à l'exécution</b> " +
                "(ils ne sont pas configurés ici).",
            )
            .appendTo(wrap);

        // Bloc strategyId
        const idRow = $("<div></div>")
            .css({display: "flex", alignItems: "center", gap: "8px", margin: "12px 0"})
            .appendTo(wrap);
        $("<label></label>").text("Identifiant de stratégie").css({fontWeight: 600}).appendTo(idRow);
        const idValue = this.#model.Primarykey ?? "(non enregistrée)";
        const idBox = $("<code></code>")
            .text(idValue)
            .css({
                background: "#f3f3f3",
                padding: "4px 8px",
                borderRadius: "4px",
                fontFamily: "monospace",
            })
            .appendTo(idRow);
        $("<button class='k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base'>Copier</button>")
            .on("click", (e) => {
                e.preventDefault();
                navigator.clipboard
                    .writeText(idBox.text())
                    .then(() => NotificationHelper.Instance.success("Id copié"));
            })
            .appendTo(idRow);

        // Note : variables réservées vs contexte
        $("<p></p>")
            .css({color: "#555", marginTop: "12px"})
            .html(
                "Le template d'envoi est fusionné avec le <code>context</code> fourni ici : " +
                "placez-y toutes les variables attendues par le template (<code>{{Nom}}</code>, " +
                "<code>{{NumCommande}}</code>…). EASYMAIL y ajoute les <b>variables réservées</b> " +
                "<code>{{Code}}</code> (le code OTP généré) et <code>{{ExpiresAt}}</code> (date " +
                "d'expiration) : elles sont <b>prioritaires</b> et ne peuvent pas être fournies par " +
                "l'appelant — le code ne provient que du serveur.",
            )
            .appendTo(wrap);

        // Exemple de payload
        $("<div></div>")
            .text("Exemple de payload d'appel")
            .css({fontWeight: 600, marginTop: "12px", marginBottom: "4px"})
            .appendTo(wrap);

        const pre = $("<pre></pre>")
            .css({
                background: "#1e1e1e",
                color: "#dcdcdc",
                padding: "14px",
                borderRadius: "6px",
                fontSize: "13px",
                overflowX: "auto",
            })
            .text(this.#payloadExample())
            .appendTo(wrap);

        $("<button class='k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base'>Copier le payload</button>")
            .css({marginTop: "8px"})
            .on("click", (e) => {
                e.preventDefault();
                navigator.clipboard
                    .writeText(pre.text())
                    .then(() => NotificationHelper.Instance.success("Payload copié"));
            })
            .appendTo(wrap);

        // ---- Réponse de /otp/verify (synchrone) ----
        $("<hr/>").css({margin: "20px 0", border: "none", borderTop: "1px solid #eee"}).appendTo(wrap);

        $("<div></div>")
            .text("Réponse de la vérification (POST /otp/verify)")
            .css({fontWeight: 600, marginBottom: "4px"})
            .appendTo(wrap);

        $("<p></p>")
            .css({color: "#555", marginTop: 0})
            .html(
                "EASYMAIL répond <b>de façon synchrone</b> à l'appel <code>/otp/verify</code> de " +
                "l'application : la réussite, c'est simplement <code>Verified: true</code>. EASYMAIL " +
                "<b>n'appelle aucune URL de retour</b> et ne renvoie aucun jeton : c'est un <b>oracle " +
                "oui/non sur le code</b>. La gestion de session (déblocage de la ressource, anti-rejeu) " +
                "appartient à l'application appelante.",
            )
            .appendTo(wrap);

        const preRet = $("<pre></pre>")
            .css({
                background: "#1e1e1e",
                color: "#dcdcdc",
                padding: "14px",
                borderRadius: "6px",
                fontSize: "13px",
                overflowX: "auto",
            })
            .text(this.#verifyExample())
            .appendTo(wrap);

        $("<button class='k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base'>Copier la réponse</button>")
            .css({marginTop: "8px"})
            .on("click", (e) => {
                e.preventDefault();
                navigator.clipboard
                    .writeText(preRet.text())
                    .then(() => NotificationHelper.Instance.success("Réponse copiée"));
            })
            .appendTo(wrap);

        $("<div></div>")
            .css({
                marginTop: "16px",
                padding: "8px 12px",
                background: "#fff7e6",
                border: "1px solid #ffe0a3",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#7a5b00",
            })
            .html(
                "<b>callbackUrl / redirection :</b> non implémenté à ce jour. Le paramètre " +
                "<code>callbackUrl</code> est accepté et conservé, mais EASYMAIL ne l'appelle jamais. " +
                "Ce mécanisme (retour type « payment return ») ne servira que le jour où EASYMAIL " +
                "hébergera lui-même la page de saisie du code. Voir <code>docs/OTP.md</code>.",
            )
            .appendTo(wrap);
    };

    public Dispose = () => {
        if (this.#jqMainContainer) this.#jqMainContainer.empty();
    };
}

export interface IOtpContractEditorOptions {
    jqMainContainer: JQuery;
    model: IOtpStrategyModel;
}
